import { useEffect, useState, useSyncExternalStore } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router';

import { useVoiceCopy } from '@/platform/voice/use-voice';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type UnsavedEntry = {
  id: number;
  when: boolean;
  onDiscard: () => void;
};

let nextEntryId = 1;
let unsavedEntries: readonly UnsavedEntry[] = [];
const unsavedListeners = new Set<() => void>();

function emitUnsavedEntries(): void {
  for (const listener of unsavedListeners) {
    listener();
  }
}

function subscribeUnsavedEntries(listener: () => void): () => void {
  unsavedListeners.add(listener);
  return () => {
    unsavedListeners.delete(listener);
  };
}

function getUnsavedEntries(): readonly UnsavedEntry[] {
  return unsavedEntries;
}

/**
 * Registers one unsaved form. React Router allows a single navigation blocker,
 * so several cards share {@link ProfileUnsavedBlockerHost}.
 */
export function ProfileUnsavedBlocker({
  when,
  onDiscard,
}: {
  when: boolean;
  onDiscard: () => void;
}) {
  const [id] = useState(() => nextEntryId++);

  useEffect(() => {
    unsavedEntries = [
      ...unsavedEntries.filter((entry) => entry.id !== id),
      { id, when, onDiscard },
    ];
    emitUnsavedEntries();
    return () => {
      unsavedEntries = unsavedEntries.filter((entry) => entry.id !== id);
      emitUnsavedEntries();
    };
  }, [id, when, onDiscard]);

  return null;
}

/** One dialog for every registered profile form. Mount once beside the profile routes. */
export function ProfileUnsavedBlockerHost() {
  const copy = useVoiceCopy();
  const entries = useSyncExternalStore(
    subscribeUnsavedEntries,
    getUnsavedEntries,
    getUnsavedEntries,
  );
  const when = entries.some((entry) => entry.when);
  const blocker = useBlocker(when);
  const open = blocker.state === 'blocked';

  useBeforeUnload(
    (event) => {
      if (when) {
        event.preventDefault();
      }
    },
    { capture: true },
  );

  useEffect(() => {
    if (!when && blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [when, blocker.state, blocker]);

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && blocker.state === 'blocked') {
          blocker.reset();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Ungespeicherte Änderungen</AlertDialogTitle>
          <AlertDialogDescription>{copy('platform.profile.unsaved')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              if (blocker.state === 'blocked') {
                blocker.reset();
              }
            }}
          >
            Bleiben
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              for (const entry of entries) {
                if (entry.when) {
                  entry.onDiscard();
                }
              }
              if (blocker.state === 'blocked') {
                blocker.proceed();
              }
            }}
          >
            Verwerfen
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
